import styled from 'styled-components';
import {useMyCouponHistory} from '@/api/coupon';
import {Typography, typographyStyle} from '@/components/Typography';
import {COUPON_HISTORY_STATUS, COUPON_MESSAGES} from '@/constants';
import {useSession} from '@/hooks';
import {
  DashRoundedBox,
  Flexbox,
  FlexFullGrow,
  GrayRoundedBox,
  interceptProps,
} from '@/styled-components';
import {palette} from '@/styles';
import {getCouponHistoryPeriodText, getCouponDisplayName} from '@/utils';
import {Box} from './StyledComponents';

/**
 * 마이페이지 `쿠폰 이용 이력` (조회 전용).
 * 사용을 시작한 적이 있는 쿠폰만 시작일 최신순으로 표시하며, 선택·사용 동작은 제공하지 않는다.
 */
export const CouponHistory = () => {
  const {user} = useSession();
  const {data, isPending} = useMyCouponHistory(user?.id);

  const isEmpty = !data?.length;

  return (
    <Box>
      <Typography variant="bold14">쿠폰 이용 이력</Typography>
      {isPending || isEmpty ? (
        <DotBox>
          <Typography variant="bold16" color={palette.gray400}>
            {isPending
              ? COUPON_MESSAGES.historyLoading
              : COUPON_MESSAGES.historyEmpty}
          </Typography>
        </DotBox>
      ) : (
        <GrayBox>
          {data?.map((i, idx) => (
            <Row key={idx} gap="8px" alignItems="flex-start">
              <div>
                <CouponName>{getCouponDisplayName(i)}</CouponName>
                <Typography variant="medium12" color={palette.gray600}>
                  {getCouponHistoryPeriodText(i)}
                </Typography>
              </div>
              <FlexFullGrow />
              <StatusText statusColor={COUPON_HISTORY_STATUS[i.status]?.color}>
                {COUPON_HISTORY_STATUS[i.status]?.label}
              </StatusText>
            </Row>
          ))}
        </GrayBox>
      )}
    </Box>
  );
};

const DotBox = styled(DashRoundedBox)`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const GrayBox = styled(GrayRoundedBox)`
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  ${{...typographyStyle.medium14}}
`;

const Row = styled(Flexbox)`
  padding: 16px 0;
  & + & {
    border-top: solid 1px ${palette.gray150};
  }
`;

const CouponName = styled.div`
  color: ${palette.gray900};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
`;

const StatusText = styled('div').withConfig({
  shouldForwardProp: interceptProps(['statusColor']),
})<{statusColor?: string}>`
  flex-shrink: 0;
  color: ${({statusColor}) => statusColor || palette.gray600};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
`;
